import { useEffect, useState } from 'react';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { SelectorDeIcono } from '@/components/SelectorDeIcono';
import { tintaSobre } from './piezas';
import { useAreas, useGuardarComunidad, type Comunidad } from './api';

/**
 * FormularioDeComunidad.tsx — crear y editar. Módulo 9, opción 3.
 *
 * ## Los dos `<textarea>` ya no escriben HTML crudo
 *
 * `description` y `rules` se interpolaban sin escapar dentro del `<textarea>`
 * del formulario vanilla, así que un `</textarea` literal se salía del campo.
 * Lo escribe un **moderador** —que no tiene por qué ser staff— y lo abre quien
 * edita, que sí suele serlo. Se arregló el 2026-09-18 en su propio cambio; aquí
 * el problema desaparece de raíz, porque en React el texto de un `value` no es
 * marcado.
 *
 * ## El color ya no decide si el título se lee
 *
 * El selector sigue siendo libre —así lo quiso quien lo pidió— pero la portada
 * elige su tinta según la luminancia del color. Antes el título iba siempre en
 * blanco: con un amarillo quedaba en 1,40 de contraste. La previsualización de
 * aquí abajo enseña exactamente lo que se va a ver.
 *
 * ## Sin estado en `window`
 *
 * El vanilla guardaba el id de la comunidad en `window._commMembersId` y leía
 * los campos del DOM con `getElementById` al guardar. `Convenciones.md` lo
 * prohíbe por nombre; aquí el estado vive y muere con el formulario.
 */

/** `CommunityBody` exige nombre no vacío; la columna es `String(120)`. */
const MAXIMO_NOMBRE = 120;
const COLOR_POR_DEFECTO = '#0D2B4E';

export function FormularioDeComunidad({
  abierto, comunidad, onCerrar, onCreada,
}: {
  abierto: boolean;
  /** `null` = crear. */
  comunidad: Comunidad | null;
  onCerrar: () => void;
  /** Solo al crear: la pantalla decide a dónde ir con la recién creada. */
  onCreada?: (c: Comunidad) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [reglas, setReglas] = useState('');
  const [areaId, setAreaId] = useState('');
  const [privada, setPrivada] = useState(false);
  const [icono, setIcono] = useState('bi-people');
  const [color, setColor] = useState(COLOR_POR_DEFECTO);
  const [error, setError] = useState('');

  // Las áreas solo se piden cuando el formulario se abre: quien nunca crea una
  // comunidad no se las descarga.
  const areas = useAreas(abierto);
  const guardar = useGuardarComunidad();

  useEffect(() => {
    if (!abierto) return;
    setNombre(comunidad?.name ?? '');
    setDescripcion(comunidad?.description ?? '');
    setReglas(comunidad?.rules ?? '');
    setAreaId(comunidad?.area_id != null ? String(comunidad.area_id) : '');
    setPrivada(comunidad?.is_private ?? false);
    setIcono(comunidad?.icon || 'bi-people');
    setColor(comunidad?.color || COLOR_POR_DEFECTO);
    setError('');
  }, [abierto, comunidad]);

  function enviar() {
    const limpio = nombre.trim();
    if (!limpio) {
      setError('Escribe un nombre para la comunidad.');
      return;
    }
    guardar.mutate(
      {
        id: comunidad?.id ?? null,
        cuerpo: {
          name: limpio,
          description: descripcion.trim() || null,
          rules: reglas.trim() || null,
          area_id: areaId ? Number(areaId) : null,
          is_private: privada,
          icon: icono || 'bi-people',
          color,
        },
      },
      {
        onSuccess: (creada) => {
          Utils.toast(comunidad ? 'Comunidad actualizada' : 'Comunidad creada', 'success');
          if (!comunidad && onCreada) onCreada(creada);
          else onCerrar();
        },
        onError: (err) =>
          Utils.toast(
            err instanceof Error ? err.message : 'No se pudo guardar la comunidad',
            'error',
          ),
      },
    );
  }

  const tinta = tintaSobre(color);

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo={comunidad ? 'Editar comunidad' : 'Nueva comunidad'}
      descripcion={
        comunidad ? comunidad.name : 'Un espacio con su propio muro, sus miembros y sus reglas.'
      }
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={guardar.isPending}>
            <i className="bi bi-check-lg" aria-hidden="true" />{' '}
            {comunidad ? 'Guardar cambios' : 'Crear comunidad'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Nombre" error={error}>
          <Input
            value={nombre}
            maxLength={MAXIMO_NOMBRE}
            onChange={(e) => { setNombre(e.target.value); setError(''); }}
            placeholder="Ej: Club de Lectura"
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <Field label="Ícono" ayuda="Se ve en la portada y en la rejilla.">
            <SelectorDeIcono id="cm-icono" valor={icono} onCambio={setIcono} respaldo="bi-people" />
          </Field>
          <Field label="Color de portada">
            <input
              type="color"
              aria-label="Color de portada"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-11 w-16 cursor-pointer rounded-sm border border-field-border bg-surface p-1"
            />
          </Field>
        </div>

        {/* La previsualización no es adorno: es lo único que enseña, ANTES de
            guardar, cómo queda el nombre sobre el color elegido. */}
        <div
          className="flex items-center gap-3 rounded-sm p-3"
          style={{ background: color, color: tinta.texto }}
        >
          <i className={`bi ${icono || 'bi-people'} text-7 leading-none`} aria-hidden="true" />
          <span className="min-w-0 truncate text-4 font-bold">
            {nombre.trim() || 'Así se verá la portada'}
          </span>
        </div>

        <Field label="Descripción" opcional ayuda="Una o dos frases sobre para qué existe.">
          <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        </Field>

        <Field
          label="Reglas de convivencia"
          opcional
          ayuda="Las verán todos los miembros en la cabecera."
        >
          <Textarea
            rows={3}
            value={reglas}
            onChange={(e) => setReglas(e.target.value)}
            placeholder="Normas de la comunidad…"
          />
        </Field>

        <Field
          label="Área"
          opcional
          ayuda={areas.isError ? 'No se pudieron cargar las áreas. Puedes guardar sin área.' : undefined}
        >
          <Select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
            <option value="">— Sin área —</option>
            {(areas.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        </Field>

        <label className="flex min-h-11 cursor-pointer items-start gap-2.5 text-3 text-ink-2">
          <input
            type="checkbox"
            checked={privada}
            onChange={(e) => setPrivada(e.target.checked)}
            className="mt-1 size-4 accent-brand"
          />
          <span>
            <span className="font-semibold text-ink">Privada</span>
            <span className="block text-2 text-muted">
              No aparece en la lista de quien no es miembro, y solo se entra por invitación de un
              moderador.
            </span>
          </span>
        </label>
      </div>
    </Drawer>
  );
}
